import {
  IS_BROWSER,
  getNewEmptyIframe,
  getOriginFromUrl,
  getCurrentPageRootUrl,
  attachMessageHandler,
} from './browserUtils';
import { MissingRefreshTokenError } from './MissingRefreshTokenError';
import { TokenEndpointError } from './TokenEndpointError';

const IMPLICIT_TOKEN_REFRESH_TIMEOUT_MS = 10 * 1000;

export type LocalStorageKey =
  | 'oc-sdk-access-token'
  | 'oc-sdk-refresh-token'
  | 'oc-sdk-access-token-expiration'
  | 'oc-sdk-access-token-requested-scopes'
  | 'oc-sdk-access-token-scopes'
  | 'oc-sdk-access-token-grant'
  | 'oc-sdk-redirect-uri';

export type Scope =
  | 'user_email'
  | 'user_public_profile'
  | 'user_learning_activity'
  | 'learning_content'
  | 'openclassrooms_client'
  | 'user_create_financial_aid'
  | 'user_birth_information'
  | 'user_communications'
  | 'user_calendar'
  | 'organization'
  | 'user_interest';

export type GrantType =
  | 'password'
  | 'client_credentials'
  | 'refresh_token'
  | 'implicit'
  | 'authorization_code';

export type SetOauth2TokensParams = {
  accessToken?: string | null;
  refreshToken?: string | null;
  scopes?: Scope[];
  grantType?: GrantType | null;
} & (
  | {
      expiresIn?: number;
      expiresAt?: never;
    }
  | {
      expiresIn?: never;
      expiresAt?: number;
    }
);

type RequestOptions =
  | {
      grantType: 'password';
      username: string;
      password: string;
      scopes: Scope[];
    }
  | {
      grantType: 'client_credentials';
      scopes: Scope[];
    }
  | {
      grantType: 'authorization_code';
      code: string;
      redirectUri: string;
      requireRefreshToken?: boolean;
      scopes: Scope[];
    }
  | {
      grantType: 'refresh_token';
      refreshToken: string;
    };

export interface OAuthToken {
  accessToken: string | null;
  refreshToken: string | null;
  expiredAt: number;
  scopes: Scope[];
  grantType: GrantType | null;
}

export interface APIOAuthTokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
}

export type OnNewAccessTokenCallback = (tokenData: OAuthToken) => void;

type RequestData = {
  url: string;
  method: 'POST';
  headers: Record<string, string>;
  body: any;
};

type OAuthAuthorizeResponseType = 'token' | 'code';

type OAuthAuthorizeOptions = {
  redirectUri?: string;
  responseType?: OAuthAuthorizeResponseType;
  scopes?: Scope[];
  state?: string;
};

export type RequestInterceptor = (requestData: RequestData) => RequestData;

class OCAuthSDK {
  protected clientId: string;

  protected clientSecret?: string;

  protected rootUrl: string;

  protected authorizeUrl: string;

  public oAuthToken: OAuthToken;

  protected onNewAccessTokenCallbacks: OnNewAccessTokenCallback[];

  protected refreshImplicitTokenPromise: Promise<void> | null;

  protected refreshTokenPromise: Promise<OAuthToken> | null;

  protected requestInterceptors: RequestInterceptor[];

  static storageCleanUp(): void {
    if (!IS_BROWSER) {
      return;
    }
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const fullKey = window.localStorage.key(i);
      if (fullKey?.includes('oc-sdk-')) {
        keysToRemove.push(fullKey);
      }
    }
    keysToRemove.forEach((k) => window.localStorage.removeItem(k));
  }

  constructor({
    clientId,
    clientSecret,
    rootUrl = 'https://api.openclassrooms.com',
    authorizeUrl = 'https://openclassrooms.com/oauth2/authorize',
  }: {
    clientId: string;
    clientSecret?: string;
    rootUrl?: string;
    authorizeUrl?: string;
  }) {
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.rootUrl = rootUrl;
    this.authorizeUrl = authorizeUrl;
    this.onNewAccessTokenCallbacks = [];
    this.refreshImplicitTokenPromise = null;
    this.refreshTokenPromise = null;
    this.requestInterceptors = [];

    this.oAuthToken = {
      accessToken: null,
      refreshToken: null,
      expiredAt: 0,
      scopes: [],
      grantType: null,
    };

    if (IS_BROWSER) {
      if (!this.setOauth2TokensFromUrl()) {
        this.setOauth2TokensFromLocalStorage();
      }
    }
  }

  protected setOauth2TokensFromLocalStorage() {
    const accessTokenExpiration = this.getStorageItem('oc-sdk-access-token-expiration') || null;

    // TODO: handle transition between "oc-sdk-access-token-scope" (a number)
    // and "oc-sdk-access-token-scopes" (a space-separated string of scopes)
    const scopes = this.getStorageItem('oc-sdk-access-token-scopes') || '';

    this.setOauth2Tokens({
      accessToken: this.getStorageItem('oc-sdk-access-token') || null,
      refreshToken: this.getStorageItem('oc-sdk-refresh-token') || null,
      expiresAt: accessTokenExpiration ? Number(accessTokenExpiration) : 0,
      scopes: scopes ? (scopes.split(' ') as Scope[]) : [],
      grantType: this.getStorageItem<GrantType>('oc-sdk-access-token-grant') || null,
    });
  }

  protected prependStringWithClientId<T extends string>(source: T): `${string}__${T}` {
    return `${this.clientId}__${source}`;
  }

  protected getStorageItem<T extends string = string>(storageKey: LocalStorageKey): T | null {
    const item = window.localStorage.getItem(this.prependStringWithClientId(storageKey));

    if (item) {
      return item as T;
    }

    return null;
  }

  protected setStorageItem(storageKey: LocalStorageKey, itemValue: string | number): void {
    window.localStorage.setItem(this.prependStringWithClientId(storageKey), `${itemValue}`);
  }

  protected removeStorageItem(storageKey: LocalStorageKey): void {
    window.localStorage.removeItem(this.prependStringWithClientId(storageKey));
  }

  setRootUrl(rootUrl: string): this {
    this.rootUrl = rootUrl;
    return this;
  }

  setAuthorizeUrl(authorizeUrl: string): this {
    this.authorizeUrl = authorizeUrl;
    return this;
  }

  setClientIdSecret(clientId: string, clientSecret?: string): this {
    this.clientId = clientId;
    this.clientSecret = clientSecret;

    this.setOauth2TokensFromLocalStorage();

    return this;
  }

  getBasicAuthorization(): string {
    return btoa(`${this.clientId}:${this.clientSecret}`);
  }

  async request(body: Record<string, string | number> = {}): Promise<APIOAuthTokenResponse> {
    const headersToSend = {
      Accept: 'application/json',
      Authorization: `Basic ${this.getBasicAuthorization()}`,
      'Content-Type': 'application/json',
    };

    const bodyToSend = body ? JSON.stringify(body) : '{}';

    const requestData = this.applyRequestInterceptors({
      url: `${this.rootUrl}/oauth2/token`,
      method: 'POST',
      headers: headersToSend,
      body: bodyToSend,
    });

    const { url: finalURL, ...requestOptions } = requestData;

    const init: RequestInit = {
      credentials: 'include',
      redirect: 'manual',
      ...requestOptions,
    };

    const response = await fetch(finalURL, init);

    if (response.type === 'opaqueredirect' || response.status === 302) {
      throw new TokenEndpointError('[oc-auth-sdk] Cannot get access token.', response);
    }

    if (!response.ok) {
      throw new TokenEndpointError('[oc-auth-sdk] Token endpoint rejected the request.', response);
    }

    return response.json();
  }

  setOauth2Tokens({
    accessToken = null,
    refreshToken = null,
    expiresIn = 0,
    expiresAt = 0,
    scopes = [],
    grantType = null,
  }: SetOauth2TokensParams = {}): this {
    const expirationDate = expiresIn ? Date.now() + expiresIn * 1000 : expiresAt;

    this.oAuthToken = {
      accessToken,
      refreshToken,
      expiredAt: expirationDate,
      scopes,
      grantType,
    };

    this.dispatchNewAccessToken();

    if (!IS_BROWSER) {
      return this;
    }

    if (accessToken) {
      this.setStorageItem('oc-sdk-access-token', accessToken);
    } else {
      this.removeStorageItem('oc-sdk-access-token');
    }

    if (refreshToken) {
      this.setStorageItem('oc-sdk-refresh-token', refreshToken);
    } else {
      this.removeStorageItem('oc-sdk-refresh-token');
    }

    if (expirationDate) {
      this.setStorageItem('oc-sdk-access-token-expiration', expirationDate);
    } else {
      this.removeStorageItem('oc-sdk-access-token-expiration');
    }

    if (scopes.length) {
      this.setStorageItem('oc-sdk-access-token-scopes', scopes.join(' '));
    } else {
      this.removeStorageItem('oc-sdk-access-token-scopes');
    }

    if (grantType) {
      this.setStorageItem('oc-sdk-access-token-grant', grantType);
    } else {
      this.removeStorageItem('oc-sdk-access-token-grant');
    }

    return this;
  }

  protected setOauth2TokensFromUrl(): boolean {
    const queryParams = new URLSearchParams(window.location.hash.substring(1));

    const scopes = this.getStorageItem('oc-sdk-access-token-requested-scopes');

    if (!scopes?.length || !queryParams.has('access_token')) {
      return false;
    }

    this.removeStorageItem('oc-sdk-access-token-requested-scopes');

    this.setOauth2Tokens({
      accessToken: queryParams.get('access_token'),
      refreshToken: queryParams.get('refresh_token'),
      expiresIn: Number(queryParams.get('expires_in')),
      grantType: 'authorization_code',
      scopes: scopes.split(' ') as Scope[],
    });

    return true;
  }

  public getAccessToken(): string | null {
    return this.oAuthToken.accessToken;
  }

  public hasAccessToken(): boolean {
    return Boolean(this.getAccessToken()) && this.oAuthToken.scopes.length > 0;
  }

  async requestToken(options: RequestOptions): Promise<OAuthToken> {
    const bodyParams: {
      grant_type: GrantType;
      scope?: string;
      username?: string;
      password?: string;
      refresh_token?: string;
      code?: string;
      redirect_uri?: string;
    } = {
      grant_type: options.grantType,
    };

    if (options.grantType === 'password') {
      bodyParams.username = options.username;
      bodyParams.password = options.password;
    }

    if (options.grantType === 'authorization_code') {
      bodyParams.code = options.code;
      bodyParams.redirect_uri = options.redirectUri;
    }

    const isRefreshToken = options.grantType === 'refresh_token';

    if (isRefreshToken) {
      bodyParams.refresh_token = options.refreshToken;
    } else if (options.grantType !== 'authorization_code') {
      bodyParams.scope = options.scopes.join(' ');
    }

    const oAuthToken = await this.request(bodyParams);

    if (!oAuthToken.access_token) {
      throw new Error('[oc-auth-sdk] Token response is missing access token.');
    }

    if (
      options.grantType === 'authorization_code' &&
      options.requireRefreshToken &&
      !oAuthToken.refresh_token
    ) {
      throw new Error('[oc-auth-sdk] Token response is missing refresh token.');
    }

    const accessTokenData: Parameters<OCAuthSDK['setOauth2Tokens']>[0] = {
      accessToken: oAuthToken.access_token,
      refreshToken:
        oAuthToken.refresh_token ?? (isRefreshToken ? this.oAuthToken.refreshToken : null),
      expiresIn: oAuthToken.expires_in ?? 0,
      grantType: options.grantType,
    };

    accessTokenData.scopes = isRefreshToken ? this.oAuthToken.scopes : options.scopes;

    this.setOauth2Tokens(accessTokenData);

    return this.oAuthToken;
  }

  onNewAccessToken(callback: OnNewAccessTokenCallback): void {
    this.onNewAccessTokenCallbacks.push(callback);
  }

  dispatchNewAccessToken(): void {
    this.onNewAccessTokenCallbacks.forEach((callback) => callback(this.oAuthToken));
  }

  isAccessTokenExpired(): boolean {
    const { expiredAt } = this.oAuthToken;

    // We check expiration 30mn before real expiration
    const thirtyMinutes = 1000 * 60 * 30;
    const isTokenExpired = Date.now() >= (expiredAt || 0) - thirtyMinutes;

    return isTokenExpired;
  }

  public getCompleteAuthorizeUrl({ responseType = 'token' }: OAuthAuthorizeOptions = {}): string {
    let url = `${this.authorizeUrl}?response_type=${responseType}`;
    url += `&client_id=${encodeURIComponent(this.clientId)}`;

    return url;
  }

  public getAuthorizeUrl({
    redirectUri = getCurrentPageRootUrl(),
    responseType = 'token',
    scopes = [],
    state,
  }: OAuthAuthorizeOptions = {}): string {
    const encodedRedirectUri = encodeURIComponent(redirectUri);
    const stringifiedScopes = scopes.join(' ');

    let redirectUrl = this.getCompleteAuthorizeUrl({ responseType });

    if (stringifiedScopes) {
      redirectUrl += `&scope=${encodeURIComponent(stringifiedScopes)}`;
    }

    redirectUrl += `&redirect_uri=${encodedRedirectUri}`;

    if (state) {
      redirectUrl += `&state=${encodeURIComponent(state)}`;
    }

    return redirectUrl;
  }

  protected getAuthorizeIframeMessagesHandler(
    targetOrigin: string,
    callbackToken: string,
    iframe: HTMLIFrameElement,
    onSuccess: () => void,
    onError: () => void
  ): (e: MessageEvent) => boolean | void {
    return ({ origin, data }) => {
      if (origin === targetOrigin && data.callbackToken === callbackToken) {
        iframe.parentNode?.removeChild(iframe);

        if (data.error || !data.accessToken) {
          this.setOauth2Tokens({
            grantType: 'implicit',
          });
          this.removeStorageItem('oc-sdk-redirect-uri');

          onError();
        } else {
          this.setOauth2Tokens({
            accessToken: data.accessToken,
            expiresIn: data.expiresIn,
            grantType: 'implicit',
            scopes: this.oAuthToken.scopes,
          });
          onSuccess();
        }

        this.refreshImplicitTokenPromise = null;

        return true;
      }

      return false;
    };
  }

  protected abandonImplicitTokenRefresh(iframe: HTMLIFrameElement): void {
    iframe.parentNode?.removeChild(iframe);

    this.setOauth2Tokens({
      grantType: 'implicit',
    });
    this.removeStorageItem('oc-sdk-redirect-uri');

    this.refreshImplicitTokenPromise = null;
  }

  public async refreshImplicitToken(): Promise<void> {
    if (!IS_BROWSER) {
      throw new Error(
        '[oc-auth-sdk] Cannot refresh token with implicit grant on non-browser environment'
      );
    }

    if (this.refreshImplicitTokenPromise !== null) {
      return this.refreshImplicitTokenPromise;
    }

    const iframe = getNewEmptyIframe();
    document.body.appendChild(iframe);

    this.refreshImplicitTokenPromise = new Promise((resolve, reject) => {
      let iframeUrl = this.getCompleteAuthorizeUrl();

      const encodedUriFromStorage = this.getStorageItem('oc-sdk-redirect-uri');
      const encodedUri = encodedUriFromStorage || encodeURIComponent(getCurrentPageRootUrl());
      const appliedScopes = this.oAuthToken.scopes.length
        ? encodeURIComponent(this.oAuthToken.scopes.join(' '))
        : '';

      const callbackToken = (Math.random() * 10).toString(16);

      iframeUrl += `&response_mode=message&prompt=none&callback_token=${callbackToken}`;
      iframeUrl += `&redirect_uri=${encodedUri}&scope=${appliedScopes}`;

      // The iframe only answers when /authorize renders our postMessage template. Anything
      // else (error page, redirect to the login form, network failure) leaves this promise
      // pending forever and deadlocks every caller awaiting a token, so we always give up
      // after a delay.
      let removeMessageListener: (() => void) | null = null;

      const timeoutId = window.setTimeout(() => {
        removeMessageListener?.();
        this.abandonImplicitTokenRefresh(iframe);

        reject(new Error('[oc-auth-sdk] Timed out while refreshing token with implicit grant'));
      }, IMPLICIT_TOKEN_REFRESH_TIMEOUT_MS);

      const settle = (callback: () => void) => () => {
        removeMessageListener?.();
        window.clearTimeout(timeoutId);
        callback();
      };

      iframe.onload = () => {
        const targetOrigin = getOriginFromUrl(iframeUrl);
        iframe.contentWindow!.postMessage('refresh_token', targetOrigin);

        const handler = this.getAuthorizeIframeMessagesHandler(
          targetOrigin,
          callbackToken,
          iframe,
          settle(resolve),
          settle(reject)
        );

        removeMessageListener = attachMessageHandler(handler.bind(this));
      };

      iframe.src = iframeUrl;
    });

    return this.refreshImplicitTokenPromise;
  }

  public async refreshToken(): Promise<OAuthToken> {
    switch (this.oAuthToken.grantType) {
      case 'password':
      case 'authorization_code':
      case 'refresh_token': {
        if (!this.oAuthToken.refreshToken) {
          throw new MissingRefreshTokenError(
            '[oc-auth-sdk] Cannot refresh token without refresh token'
          );
        }

        if (this.refreshTokenPromise !== null) {
          return this.refreshTokenPromise;
        }

        this.refreshTokenPromise = this.requestToken({
          grantType: 'refresh_token',
          refreshToken: this.oAuthToken.refreshToken,
        }).finally(() => {
          this.refreshTokenPromise = null;
        });

        return this.refreshTokenPromise;
      }

      case 'implicit': {
        if (!IS_BROWSER) {
          throw new Error(
            '[oc-auth-sdk] Cannot refresh token with implicit grant on non-browser environment'
          );
        }

        await this.refreshImplicitToken();

        break;
      }

      case 'client_credentials': {
        await this.requestToken({
          grantType: 'client_credentials',
          scopes: this.oAuthToken.scopes,
        });
        break;
      }

      default:
        throw new Error(
          `[oc-auth-sdk] Cannot refresh token with unknown grant type. Got: ${this.oAuthToken.grantType}`
        );
    }

    return this.oAuthToken;
  }

  async checkAccessTokenExpiration(): Promise<OAuthToken> {
    if (this.isAccessTokenExpired()) {
      return this.refreshToken();
    }

    return this.oAuthToken;
  }

  async getValidAccessToken(): Promise<string | null> {
    if (!this.hasAccessToken()) {
      return null;
    }

    await this.checkAccessTokenExpiration();

    return this.getAccessToken();
  }

  public login(
    scopes: Scope[],
    redirectUri: string = getCurrentPageRootUrl(),
    loginOptions: { responseType?: OAuthAuthorizeResponseType; state?: string } = {}
  ): void {
    if (!IS_BROWSER) {
      throw new Error('[oc-auth-sdk] Cannot call login() method in a non-browser environment');
    }

    const stringifiedScopes = scopes.join(' ');
    const responseType = loginOptions.responseType ?? 'token';

    this.setStorageItem('oc-sdk-access-token-requested-scopes', stringifiedScopes);
    this.setStorageItem('oc-sdk-redirect-uri', encodeURIComponent(redirectUri));

    const redirectUrl = this.getAuthorizeUrl({
      redirectUri,
      responseType,
      scopes,
      state: loginOptions.state,
    });

    OCAuthSDK.redirectToUrl(redirectUrl);
  }

  public static redirectToUrl(url: string): void {
    if (!IS_BROWSER) {
      throw new Error('[oc-auth-sdk] Cannot redirect in a non-browser environment');
    }

    try {
      window.location.href = url;
    } catch {
      // Ignore navigation errors
    }
  }

  public addRequestInterceptor(interceptor: RequestInterceptor): this {
    this.requestInterceptors.push(interceptor);
    return this;
  }

  applyRequestInterceptors({ url, method, headers, body }: RequestData): RequestData {
    return this.requestInterceptors.reduce((requestData, interceptor) => interceptor(requestData), {
      url,
      method,
      headers,
      body,
    });
  }
}

export default OCAuthSDK;
